// LEITURA DE TABELA COM CABECALHO — a forma que o export da ALECE tem.
//
// Diferente do padrao Atlas, onde a IDENTIDADE do campo e' a POSICAO, aqui a
// primeira linha nomeia as colunas e a leitura casa por nome. E' a escolha certa
// para um arquivo que e' um RELATORIO de sistema de RH: a ordem das colunas
// pode mudar entre versoes do export, e casar por posicao transformaria isso num
// deslocamento silencioso de dado.
//
// Reusa `lerLinhasDelimitadas`, que ja lida com CRLF, aspas, separador dentro de
// campo e BOM.
import { lerLinhasDelimitadas } from "../../csv-delimitado.js";
import { semZerosAEsquerda } from "../../margem-informada.js";
import { COLUNAS_ALECE_FOLHA, COLUNAS_ALECE_SERVIDOR } from "./alece-colunas.js";
export class ErroTabelaAlece extends Error {
    faltando;
    constructor(mensagem, faltando = []) {
        super(mensagem);
        this.faltando = faltando;
    }
}
function colunasDe(arquivo) {
    return arquivo === "servidor" ? COLUNAS_ALECE_SERVIDOR : COLUNAS_ALECE_FOLHA;
}
function primeiraLinhaDe(texto) {
    return texto.split(/\r\n|\n|\r/).find((l) => l.trim() !== "") ?? "";
}
/**
 * O delimitador pertence ao arquivo, nao ao pacote nem a extensao: 202608 veio
 * com virgula, 202609 com ponto e virgula. Pontua cada candidato pelos nomes de
 * coluna que o cabecalho produz; zero ou empate e' "nao reconhecido".
 */
export function detectarSeparadorAlece(texto, arquivo) {
    const conhecidas = new Set(colunasDe(arquivo).map((c) => c.nome));
    const primeiraLinha = primeiraLinhaDe(texto);
    const candidatos = [";", ",", "\t"].map((separador) => {
        const cabecalho = (lerLinhasDelimitadas(primeiraLinha, separador)[0] ?? []).map(normalizarNomeColuna);
        const pontos = new Set(cabecalho.filter((c) => conhecidas.has(c))).size;
        return { separador, pontos };
    }).sort((a, b) => b.pontos - a.pontos);
    const melhor = candidatos[0];
    if (melhor.pontos === 0 || melhor.pontos === candidatos[1].pontos) {
        return { motivo: `${arquivo}: separador não reconhecido: esperado \`;\`, \`,\` ou tabulação` };
    }
    return { separador: melhor.separador };
}
/**
 * Confere SO' o cabecalho, sem lancar e sem parsear o arquivo inteiro: e' o que
 * o `reconhecer` precisa para listar de uma vez o que falta em cada arquivo do
 * pacote. `faltando` traz os nomes das colunas obrigatorias ausentes (sem
 * prefixo); `motivo` so' existe quando o separador nao foi reconhecido.
 */
export function conferirCabecalhoAlece(texto, arquivo) {
    const sep = detectarSeparadorAlece(texto, arquivo);
    if (sep.separador === undefined)
        return { faltando: [], motivo: sep.motivo };
    const cabecalho = (lerLinhasDelimitadas(primeiraLinhaDe(texto), sep.separador)[0] ?? []).map(normalizarNomeColuna);
    return { faltando: colunasFaltando(cabecalho, colunasDe(arquivo).filter((c) => c.obrigatorio).map((c) => c.nome)) };
}
/** Le a tabela inteira; lanca `ErroTabelaAlece` no que `conferirCabecalhoAlece` reportaria. */
export function lerTabelaAlece(texto, arquivo) {
    const sep = detectarSeparadorAlece(texto, arquivo);
    if (sep.separador === undefined)
        throw new ErroTabelaAlece(sep.motivo);
    const tabela = lerTabelaComCabecalho(texto, sep.separador);
    const faltando = colunasFaltando(tabela.cabecalho, colunasDe(arquivo).filter((c) => c.obrigatorio).map((c) => c.nome));
    if (faltando.length) {
        throw new ErroTabelaAlece(`${arquivo}: colunas obrigatórias ausentes no cabeçalho: ${faltando.join(", ")}`, faltando.map((c) => `${arquivo}.${c}`));
    }
    // IDENTIDADE SEM ZEROS A ESQUERDA. Ate' 202608 a ALECE mandava `1476` e `7`;
    // o retorno de 202609 veio com MATR completada a 6 digitos e FF a 2 (`001476`,
    // `07`). Tudo o que ja' esta' no banco — contratos, servidores, fotos — guarda
    // sem zeros, e passar o valor como veio duplicou o servidor na importacao
    // (`001476` ao lado de `1476`). A borda traduz; o resto nunca ve o zero.
    //
    // O valor BRUTO fica em `identidadeOriginal`, paralelo a `registros`: e' o que
    // `margem_folha_linhas.matricula_original`/`ff_original` guardam para conferir
    // a linha contra o arquivo do ente. Os registros sao COPIADOS, nao mutados, e
    // o bruto nao vai como chave sintetica: `registros` e' chaveado pelo nome da
    // coluna do arquivo, e uma chave inventada poderia colidir com coluna real.
    const identidadeOriginal = [];
    const registros = tabela.registros.map((r) => {
        const matricula = r.MATR ?? "";
        const ff = r.FF ?? "";
        identidadeOriginal.push({ matricula, ff });
        const copia = { ...r };
        if (r.MATR !== undefined)
            copia.MATR = semZerosAEsquerda(matricula);
        if (r.FF !== undefined)
            copia.FF = semZerosAEsquerda(ff);
        return copia;
    });
    return { ...tabela, registros, identidadeOriginal };
}
/**
 * Quantos registros DESTA tabela tiveram MATR ou FF normalizados. Calculado
 * sobre os arrays, e nao guardado como contador, para valer tambem depois de
 * `filtrarTabela`: linha descartada nao conta no relatorio.
 */
export function identidadesNormalizadas(tabela) {
    const originais = tabela.identidadeOriginal ?? [];
    return tabela.registros.filter((r, i) => {
        const o = originais[i];
        return !!o && ((r.MATR ?? "") !== o.matricula || (r.FF ?? "") !== o.ff);
    }).length;
}
/** Mantém o vínculo com a linha de origem ao descartar registros inválidos. */
export function filtrarTabela(tabela, aceita) {
    const registros = [];
    const linhasDosRegistros = [];
    // Todo array indexado por registro anda junto; os contadores vao no spread.
    const identidadeOriginal = tabela.identidadeOriginal ? [] : undefined;
    tabela.registros.forEach((r, i) => {
        const linha = tabela.linhasDosRegistros[i];
        if (aceita(r, linha)) {
            registros.push(r);
            linhasDosRegistros.push(linha);
            identidadeOriginal?.push(tabela.identidadeOriginal[i]);
        }
    });
    return { ...tabela, registros, linhasDosRegistros, ...(identidadeOriginal ? { identidadeOriginal } : {}) };
}
/** Trim, caixa alta, sem BOM e sem aspas residuais. */
export function normalizarNomeColuna(bruto) {
    return bruto.replace(/^﻿/, "").replace(/^"|"$/g, "").trim().toUpperCase();
}
/**
 * Le o texto como tabela com cabecalho.
 *
 * Quantidade diferente de celulas e' reportada e descartada: tanto truncamento
 * quanto separador sem aspas podem deslocar identidade e valores monetarios.
 * A UNICA tolerancia e' o export que termina cada linha no separador (ERP,
 * Excel): exatamente uma celula VAZIA a mais que o cabecalho e' aceita, sem a
 * celula, e contada em `separadorNoFim` para o adaptador avisar. Cabecalho que
 * tambem termina no separador declara a coluna vazia, e ai' nao ha' o que
 * tolerar nem avisar. Foi este caso que a versao anterior recusava por inteiro.
 */
export function lerTabelaComCabecalho(texto, separador) {
    const linhas = lerLinhasDelimitadas(texto, separador);
    if (linhas.length === 0)
        return { cabecalho: [], registros: [], linhasDosRegistros: [], linhasIgnoradas: [], separadorNoFim: 0 };
    const cabecalhoBruto = linhas[0].map(normalizarNomeColuna);
    // Sem as celulas vazias do fim: `FF;MATR;` declara duas colunas, nao tres.
    let fim = cabecalhoBruto.length;
    while (fim > 0 && cabecalhoBruto[fim - 1] === "")
        fim--;
    const cabecalho = cabecalhoBruto.slice(0, fim);
    const cabecalhoTerminaNoSeparador = fim < cabecalhoBruto.length;
    const registros = [];
    const linhasDosRegistros = [];
    const linhasIgnoradas = [];
    let separadorNoFim = 0;
    for (let i = 1; i < linhas.length; i++) {
        let celulas = linhas[i];
        if (celulas.length === cabecalho.length + 1 && celulas[celulas.length - 1] === "") {
            celulas = celulas.slice(0, -1);
            if (!cabecalhoTerminaNoSeparador)
                separadorNoFim++;
        }
        if (celulas.length !== cabecalho.length) {
            linhasIgnoradas.push({
                linha: i + 1,
                celulas: celulas.length,
                motivo: `linha com ${celulas.length} colunas; o cabeçalho declara ${cabecalho.length}`,
            });
            continue;
        }
        const reg = {};
        for (let c = 0; c < cabecalho.length; c++)
            reg[cabecalho[c]] = (celulas[c] ?? "").trim();
        registros.push(reg);
        linhasDosRegistros.push(i + 1);
    }
    return { cabecalho, registros, linhasDosRegistros, linhasIgnoradas, separadorNoFim };
}
/** Colunas obrigatorias que o cabecalho nao tem. */
export function colunasFaltando(cabecalho, obrigatorias) {
    const presentes = new Set(cabecalho);
    return obrigatorias.filter((o) => !presentes.has(o));
}
/** Colunas do arquivo que o layout nao declara — relatadas, nunca fatais. */
export function colunasExtras(cabecalho, declaradas) {
    const conhecidas = new Set(declaradas);
    return cabecalho.filter((c) => c !== "" && !conhecidas.has(c));
}
//# sourceMappingURL=alece-tabela.js.map